import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import api from '@/services/apiClient';
import type { OnboardingState } from '@/types';

/**
 * Persistent top-bar nudge for in-progress onboarding. Renders only
 * when the tenant hasn't completed AND hasn't dismissed onboarding.
 * One click → /setup.
 *
 * Refresh strategy (three layers, in order of immediacy):
 *   1. `onboarding:refresh` window event — pages fire this after any
 *      API call that may have flipped a milestone. Gives the chip an
 *      instant update without waiting for a poll tick.
 *   2. Route change — every navigation refetches. Catches mark-on-mount
 *      visits even if the page forgets to dispatch the event.
 *   3. 30s background poll — safety net for events triggered in other
 *      tabs (e.g. an inbound call completes server-side while the user
 *      is on the dashboard). Stops once completed/dismissed.
 */
export function OnboardingChip(): JSX.Element | null {
  const [state, setState] = useState<OnboardingState | null>(null);
  const location = useLocation();
  // Keep the latest state in a ref so the (stable) polling interval
  // can read it without re-creating the interval on every state change.
  const stateRef = useRef<OnboardingState | null>(null);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    let cancelled = false;
    const c = new AbortController();
    const fetchOnce = async () => {
      try {
        const fresh = await api.onboarding.get();
        if (!cancelled) setState(fresh);
      } catch {
        /* swallowed — chip just won't render if the fetch fails */
      }
    };
    void fetchOnce();

    const onRefresh = () => {
      if (!cancelled) void fetchOnce();
    };
    window.addEventListener('onboarding:refresh', onRefresh);

    const id = window.setInterval(() => {
      const s = stateRef.current;
      if (s?.completedAt || s?.dismissedAt) return;
      void fetchOnce();
    }, 30_000);

    return () => {
      cancelled = true;
      c.abort();
      window.removeEventListener('onboarding:refresh', onRefresh);
      window.clearInterval(id);
    };
  }, []);

  // Refetch on every route change so visit-tracking on Dashboard /
  // Call History / Contacts / Billing reflects immediately in the chip,
  // even if a page forgets to dispatch the refresh event.
  useEffect(() => {
    let cancelled = false;
    void api.onboarding
      .get()
      .then((fresh) => {
        if (!cancelled) setState(fresh);
      })
      .catch(() => {
        /* swallowed */
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (!state) return null;
  if (state.completedAt || state.dismissedAt) return null;

  return (
    <Link
      to="/setup"
      title="Resume onboarding"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
        'bg-blue-100 text-blue-800 hover:bg-blue-200',
        'dark:bg-blue-500/15 dark:text-blue-300 dark:hover:bg-blue-500/25',
      )}
    >
      <Sparkles size={12} />
      Continue setup — {state.completedCount}/{state.totalCount} done
    </Link>
  );
}
